import { Copy, ShieldCheck, Link2 } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

/**
 * LotCorrespondanceField — toggle between "Unique" / "Similaire au lot XXX".
 *
 * When the user ticks "Similaire", a select lists every UNIQUE lot of the
 * project (a similar lot cannot reference another similar lot — chains are
 * banned to keep the shoebox traceability clean).
 *
 * Props
 *   • lotForm.correspondance   — current value (string)
 *   • setLotForm               — controlled setter
 *   • allLots                  — full list of project lots (we filter for Unique)
 *   • currentLotNumber         — to exclude the lot from its own reference list
 *   • compact                  — denser layout for the Config dialog
 */
const LotCorrespondanceField = ({
  lotForm,
  setLotForm,
  allLots = [],
  currentLotNumber = '',
  compact = false,
}) => {
  const isSimilar =
    lotForm.correspondance &&
    lotForm.correspondance.trim().toLowerCase() !== 'unique';

  // Only Unique lots are valid references (no chains). We also exclude the
  // lot itself from the dropdown.
  const candidates = (allLots || []).filter((l) => {
    const corr = (l.correspondance || 'Unique').trim().toLowerCase();
    return corr === 'unique' && l.lot_number !== currentLotNumber;
  });

  const handleToggle = (checked) => {
    if (checked) {
      // Pick the first available unique lot as default to avoid leaving the
      // field empty in a "Similar but no reference" half-state.
      const def = candidates[0]?.lot_number || '';
      setLotForm({ ...lotForm, correspondance: def });
    } else {
      setLotForm({ ...lotForm, correspondance: 'Unique' });
    }
  };

  return (
    <div
      className={`rounded-md ${compact ? 'p-2' : 'p-3'}`}
      style={{
        background: isSimilar ? 'rgba(56,189,248,0.06)' : 'rgba(0,0,0,0.02)',
        border: '1px solid #E5E7EB',
      }}
      data-testid="lot-correspondance-field"
    >
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!isSimilar}
          onChange={(e) => handleToggle(e.target.checked)}
          data-testid="lot-correspondance-toggle"
          className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm font-medium flex items-center gap-1.5" style={{ color: '#1F2937' }}>
          {isSimilar ? <Copy className="w-3.5 h-3.5 text-sky-500" /> : <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />}
          Ce lot est <strong className="font-semibold">similaire</strong> à un autre lot
        </span>
      </label>

      {!isSimilar ? (
        <p className={`text-[11.5px] ${compact ? 'mt-1' : 'mt-2'}`} style={{ color: '#6B7280' }}>
          Lot <span className="font-medium text-emerald-700">unique</span> : il aura son propre shoebox généré.
        </p>
      ) : (
        <div className={compact ? 'mt-2' : 'mt-3'}>
          <label className="text-[11px] text-gray-500 block mb-1 inline-flex items-center gap-1">
            <Link2 className="w-3 h-3" /> Lot de référence
          </label>
          {candidates.length === 0 ? (
            <p className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Aucun lot unique disponible dans ce projet. Crée d'abord un lot unique pour servir de référence.
            </p>
          ) : (
            <Select
              value={lotForm.correspondance}
              onValueChange={(v) => setLotForm({ ...lotForm, correspondance: v })}
            >
              <SelectTrigger className="h-8 text-sm" data-testid="lot-correspondance-select">
                <SelectValue placeholder="Choisir un lot…" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((l) => (
                  <SelectItem key={l.lot_id} value={l.lot_number}>
                    {l.lot_number} — {l.name || l.typology}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="text-[11px] mt-1.5" style={{ color: '#6B7280' }}>
            Le shoebox du lot de référence sera utilisé pour les plans de vente — pas de
            génération séparée.
          </p>
        </div>
      )}
    </div>
  );
};

export default LotCorrespondanceField;
